// ABOUTME: Props interface for custom page widget components.
// ABOUTME: Defines the contract that all widgets (node-list, node-editor, node-tree) must implement.

import { ReactElement } from 'react';

import { CustomPageFilter } from '../types';
import { parseDepthRange } from './utils/depthFilter';

export interface CardAction {
    key: string;
    label: string;
    icon: ReactElement;
    onClick: (nodeId: string) => void;
    /** Shown as an icon button on the card itself instead of inside its "more" menu. */
    inline?: boolean;
    /** `contained` renders the inline button as the app bar's white round button. */
    appearance?: 'contained';
    /** The icon and label of a node whose action shows its state, e.g. the favorite star. */
    iconFor?: (nodeId: string) => ReactElement;
    labelFor?: (nodeId: string) => string;
}

// React-router navigation state flag set when a detail widget navigates back to
// its list page. NodeListWidget reads it to suppress auto-open-single-result so
// the "back to list" path does not immediately bounce back into the detail view.
export const SKIP_AUTO_OPEN_STATE = { skipAutoOpen: true } as const;

export interface WidgetProps {
    hierarchyId: string;
    nodeId?: string;
    objectSchemaCode?: string;
    slug: string;
    filter?: CustomPageFilter;
    config?: Record<string, unknown>;
    /** Set on a `fullBleed` page: the widget fills the page height rather than sizing itself. */
    fillHeight?: boolean;
    /** Set on a `fullBleed` page, which shows no title of its own: the widget may show it. */
    pageTitle?: string;
}

// One place a card may read its image from: a content type of the node itself, or, with
// `linkContentType`, a content type of the first node that link field of the node points to.
export interface CardImageSource {
    contentType: string;
    linkContentType?: string;
}

export interface CardViewConfig {
    viewMode: 'card';
    // Cap on the cards shown; absent shows every loaded card.
    maxItems?: number;
    icon?: string;
    displayContentTypes?: string[];
    title?: string;
    showAddButton?: boolean;
    // Renders a search field above the cards that filters them by their displayed texts.
    showSearch: boolean;
    // Image sources tried in order, the first one with a value wins: `imageContentType` is either
    // that list or a single content type code of the node. Empty for a card without image.
    imageSources: CardImageSource[];
    cardVariant: 'standard' | 'highlight';
    direction: 'row' | 'column';
    cardWidth?: string;
    cardHeight?: string;
    // Space between the lines and between the cards of a line, as CSS sizes.
    rowGap: string;
    columnGap: string;
    // In a row, cards grow into the space left on their line; false keeps them at `cardWidth`.
    stretchCards: boolean;
    cardBgColor?: string;
    cardTextColor?: string;
    // Renders the first two display content type values joined by a space on the title
    // line, so a card can show e.g. "first_name last_name" as one line instead of two.
    mergeTitle: boolean;
    // Content type holding the node's type, used to pick the fallback icon of a card
    // without an image. Defaults to the second display content type.
    typeContentType?: string;
    // Lays the cards on a grid of this many equal columns instead of the `direction` flow, so
    // every card has the same size whatever the number of cards on the last line.
    columnsPerRow?: number;
    // Order of the cards: a system field (`updatedAt`, `listName`, `code`) or a content field as
    // `<contentTypeGroup>-<contentType>`. Absent = last updated first.
    sort?: CardViewSort;
    // Renders a "Load more" button below the cards that shows `maxItems` more on each click.
    loadMore: boolean;
}

export interface CardViewSort {
    field: string;
    order: 'asc' | 'desc';
}

const DEFAULT_CARD_GAP = '8px';

function parseCardImageSources(imageContentType: unknown): CardImageSource[] {
    if (typeof imageContentType === 'string') {
        return imageContentType !== '' ? [{ contentType: imageContentType }] : [];
    }
    if (!Array.isArray(imageContentType)) return [];
    return imageContentType.flatMap((source: unknown): CardImageSource[] => {
        if (typeof source !== 'object' || source === null) return [];
        const { contentType, linkContentType } = source as Record<string, unknown>;
        if (typeof contentType !== 'string' || contentType === '') return [];
        return [
            typeof linkContentType === 'string' && linkContentType !== ''
                ? { contentType, linkContentType }
                : { contentType },
        ];
    });
}

export function parseCardViewSort(sort: unknown): CardViewSort | undefined {
    if (!sort || typeof sort !== 'object') return undefined;
    const { field, order } = sort as Record<string, unknown>;
    if (typeof field !== 'string' || field === '') return undefined;
    return { field, order: order === 'asc' ? 'asc' : 'desc' };
}

interface CardViewDefaults {
    // Flow a widget falls back to when its config does not state one: favorite lists read as a
    // horizontal strip of shortcuts, while the other card lists stack vertically.
    direction?: 'row' | 'column';
}

// Card view defaults of the widgets that differ from the shared ones, keyed by widget type; read
// by the widget itself and by the loading placeholder shown while its chunk downloads.
export const CARD_VIEW_DEFAULTS_BY_WIDGET: Record<string, CardViewDefaults> = {
    'favorite-nodes': { direction: 'row' },
};

export function parseCardViewConfig(
    config?: Record<string, unknown>,
    defaults?: CardViewDefaults,
): CardViewConfig | null {
    if (!config || config.viewMode !== 'card') return null;
    return {
        viewMode: 'card',
        maxItems:
            typeof config.maxItems === 'number' && config.maxItems > 0
                ? config.maxItems
                : undefined,
        icon: typeof config.icon === 'string' ? config.icon : undefined,
        displayContentTypes: Array.isArray(config.displayContentTypes)
            ? (config.displayContentTypes as string[])
            : undefined,
        title: typeof config.title === 'string' ? config.title : undefined,
        showAddButton: typeof config.showAddButton === 'boolean' ? config.showAddButton : false,
        showSearch: config.showSearch === true,
        imageSources: parseCardImageSources(config.imageContentType),
        cardVariant: config.cardVariant === 'highlight' ? 'highlight' : 'standard',
        direction:
            config.direction === 'row' || config.direction === 'column'
                ? config.direction
                : defaults?.direction ?? 'column',
        cardWidth: typeof config.cardWidth === 'string' ? config.cardWidth : undefined,
        cardHeight: typeof config.cardHeight === 'string' ? config.cardHeight : undefined,
        rowGap: typeof config.rowGap === 'string' ? config.rowGap : DEFAULT_CARD_GAP,
        columnGap: typeof config.columnGap === 'string' ? config.columnGap : DEFAULT_CARD_GAP,
        stretchCards: config.stretchCards !== false,
        cardBgColor: typeof config.cardBgColor === 'string' ? config.cardBgColor : undefined,
        cardTextColor: typeof config.cardTextColor === 'string' ? config.cardTextColor : undefined,
        mergeTitle: config.mergeTitle === true,
        typeContentType:
            typeof config.typeContentType === 'string' ? config.typeContentType : undefined,
        columnsPerRow:
            typeof config.columnsPerRow === 'number' && config.columnsPerRow > 0
                ? Math.floor(config.columnsPerRow)
                : undefined,
        sort: parseCardViewSort(config.sort),
        loadMore: config.loadMore === true,
    };
}

export interface WidgetFrameConfig {
    title?: string;
    bgColor?: string;
    textColor?: string;
    maxHeight?: string;
    // Left undefined when the config does not mention it, so each widget can apply the
    // default that fits it (always on for dashboard-style widgets, off for full list pages).
    showViewAll?: boolean;
}

function parseCssSize(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return `${value}px`;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function parseNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

// Shared config of the widget frame: the section header title plus the styling of the
// content container that sits below it (see WidgetFrame).
export function parseWidgetFrameConfig(config?: Record<string, unknown>): WidgetFrameConfig {
    const title = parseNonEmptyString(config?.title);
    return {
        title: config?.showTitle !== false ? title : undefined,
        bgColor: parseNonEmptyString(config?.bgColor),
        textColor: parseNonEmptyString(config?.textColor),
        maxHeight: parseCssSize(config?.maxHeight),
        showViewAll: typeof config?.showViewAll === 'boolean' ? config.showViewAll : undefined,
    };
}

export interface ActionBarAction {
    type: string;
    contentTypeGroups?: string[];
    // Only for the "status" action: the workflowStatus content type to drive. Omitted when the
    // object schema has a single one, which the action auto-detects.
    contentTypeGroup?: string;
    contentType?: string;
    // Only for the "toggle-favorite" action: a round icon button instead of a labelled one.
    iconOnly?: boolean;
    // Only for the "print" action: the print template ids listed, in this order.
    templateIds?: string[];
}

export interface ActionBarInfoItem {
    contentTypeCode: string;
    label?: string;
    icon?: string;
}

export interface ActionBarConfig {
    actions: ActionBarAction[];
    backgroundColor: 'primary' | 'transparent';
    showTitle: boolean;
    titleContentTypeCode?: string;
    // `section` lays the bar out as a section header: the title with its accent mark and a
    // divider below, the actions on its right in the light style.
    titleStyle: 'plain' | 'section';
    infoItems: ActionBarInfoItem[];
}

export interface ChartWidgetConfig {
    chartType: 'bar' | 'pie' | 'line';
    dimensionContentType: { contentTypeGroup: string; contentType: string };
    measureContentType?: { contentTypeGroup: string; contentType: string };
    filters: Array<{ field: string; operator: string; value: string[] }>;
    title?: string;
    colorScheme?: string;
    hierarchyId?: string;
    // Side of the pie chart the legend sits on.
    legendPosition: ChartLegendPosition;
    // What a click on a pie slice or a bar does; absent = not clickable.
    clickAction?: ChartClickAction;
}

// "open-list" opens `path` (default: the list of the aggregated hierarchy) filtered by the clicked
// value, the chart filters and the page filter.
export type ChartClickAction = { type: 'open-list'; path?: string };

export type ChartLegendPosition = 'top' | 'bottom' | 'left' | 'right';

const CHART_LEGEND_POSITIONS: ChartLegendPosition[] = ['top', 'bottom', 'left', 'right'];

function parseChartClickAction(raw: unknown): ChartClickAction | undefined {
    if (typeof raw !== 'object' || raw === null) return undefined;
    const { type, path } = raw as { type?: unknown; path?: unknown };
    if (type !== 'open-list') return undefined;
    return typeof path === 'string' && path.trim() ? { type, path: path.trim() } : { type };
}

function isContentTypeRef(val: unknown): val is { contentTypeGroup: string; contentType: string } {
    if (typeof val !== 'object' || val === null) return false;
    const obj = val as Record<string, unknown>;
    return typeof obj.contentTypeGroup === 'string' && typeof obj.contentType === 'string';
}

export function parseChartWidgetConfig(config?: Record<string, unknown>): ChartWidgetConfig | null {
    if (!config) return null;
    const { dimensionContentType } = config;
    if (!isContentTypeRef(dimensionContentType)) return null;

    const chartType =
        config.chartType === 'pie' || config.chartType === 'line' ? config.chartType : 'bar';

    const rawFilters = Array.isArray(config.chartFilters) ? (config.chartFilters as unknown[]) : [];
    const filters = rawFilters.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as { field?: unknown; operator?: unknown; value?: unknown };
        if (typeof candidate.field !== 'string' || typeof candidate.operator !== 'string')
            return [];
        return [
            {
                field: candidate.field,
                operator: candidate.operator,
                value: Array.isArray(candidate.value) ? (candidate.value as string[]) : [],
            },
        ];
    });

    return {
        chartType,
        dimensionContentType,
        measureContentType: isContentTypeRef(config.measureContentType)
            ? config.measureContentType
            : undefined,
        filters,
        title: typeof config.title === 'string' ? config.title : undefined,
        colorScheme: typeof config.colorScheme === 'string' ? config.colorScheme : undefined,
        hierarchyId: typeof config.hierarchyId === 'string' ? config.hierarchyId : undefined,
        legendPosition: CHART_LEGEND_POSITIONS.includes(
            config.legendPosition as ChartLegendPosition,
        )
            ? (config.legendPosition as ChartLegendPosition)
            : 'bottom',
        clickAction: parseChartClickAction(config.clickAction),
    };
}

export interface MatrixAxisConfig {
    hierarchyId: string;
    label?: string;
    depthRange?: number[];
    // Whether disabled nodes are loaded for this axis. Defaults to true (disabled nodes are shown, greyed out).
    includeDisabled: boolean;
    // Neo4j node property whose value is shown as the header label. Falls back to listName when unset.
    labelProperty?: string;
}

export interface MatrixWidgetConfig {
    rows: MatrixAxisConfig;
    columns: MatrixAxisConfig;
    cellIcon?: string;
    title?: string;
    // When true, an export button downloads the current matrix view as an XLSX file.
    exportEnabled: boolean;
    // Number of HAS_LINK_TO hops that constitutes a match (1 = direct link). Defaults to 1.
    linkDepth?: number;
}

function parseAxisConfig(value: unknown): MatrixAxisConfig | null {
    if (typeof value !== 'object' || value === null) return null;
    const axis = value as Record<string, unknown>;
    if (typeof axis.hierarchyId !== 'string' || axis.hierarchyId.length === 0) return null;
    return {
        hierarchyId: axis.hierarchyId,
        label: typeof axis.label === 'string' ? axis.label : undefined,
        depthRange: parseDepthRange(axis.depthRange),
        // Disabled nodes are included by default; only an explicit false excludes them.
        includeDisabled: axis.includeDisabled !== false,
        labelProperty:
            typeof axis.labelProperty === 'string' && axis.labelProperty.length > 0
                ? axis.labelProperty
                : undefined,
    };
}

export function parseMatrixWidgetConfig(
    config?: Record<string, unknown>,
): MatrixWidgetConfig | null {
    if (!config) return null;
    const rows = parseAxisConfig(config.rows);
    const columns = parseAxisConfig(config.columns);
    if (!rows || !columns) return null;

    return {
        rows,
        columns,
        cellIcon: typeof config.cellIcon === 'string' ? config.cellIcon : undefined,
        title: typeof config.title === 'string' ? config.title : undefined,
        // Export is opt-in: only an explicit true enables the button.
        exportEnabled: config.exportEnabled === true,
        linkDepth:
            typeof config.linkDepth === 'number' && Number.isFinite(config.linkDepth)
                ? config.linkDepth
                : undefined,
    };
}

export interface TextWidgetButton {
    label: string;
    link: string;
    openInNewTab?: boolean;
    variant: 'contained' | 'text';
    color: 'primary' | 'secondary';
    fullWidth: boolean;
}

export interface TextWidgetConfig {
    /** The paragraphs of the text: one per line of the configured text. */
    paragraphs: string[];
    buttons: TextWidgetButton[];
}

/**
 * Reads the text widget config. Blank lines are dropped; a button needs a label and a link.
 * Buttons default to contained primary, full width.
 */
export function parseTextWidgetConfig(config?: Record<string, unknown>): TextWidgetConfig {
    const text = typeof config?.text === 'string' ? config.text : '';
    const rawButtons = Array.isArray(config?.buttons) ? (config?.buttons as unknown[]) : [];
    const buttons: TextWidgetButton[] = rawButtons.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as Record<string, unknown>;
        if (typeof candidate.label !== 'string' || !candidate.label) return [];
        if (typeof candidate.link !== 'string' || !candidate.link) return [];
        return [
            {
                label: candidate.label,
                link: candidate.link,
                openInNewTab: candidate.openInNewTab === true ? true : undefined,
                variant: candidate.variant === 'text' ? 'text' : 'contained',
                color: candidate.color === 'secondary' ? 'secondary' : 'primary',
                fullWidth: candidate.fullWidth !== false,
            },
        ];
    });
    return {
        paragraphs: text
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean),
        buttons,
    };
}

export interface CardGridCard {
    title: string;
    icon?: string;
    image?: string;
    link?: string;
    openInNewTab?: boolean;
    bgColor?: string;
    textColor?: string;
}

export interface CardGridConfig {
    title?: string;
    columnsPerRow: number;
    cards: CardGridCard[];
}

const DEFAULT_COLUMNS_PER_ROW = 4;

export function parseCardGridConfig(config?: Record<string, unknown>): CardGridConfig | null {
    if (!config) return null;
    const rawCards = Array.isArray(config.cards) ? (config.cards as unknown[]) : [];
    const cards: CardGridCard[] = rawCards.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as Record<string, unknown>;
        if (typeof candidate.title !== 'string' || candidate.title.length === 0) return [];
        return [
            {
                title: candidate.title,
                icon: typeof candidate.icon === 'string' ? candidate.icon : undefined,
                image: typeof candidate.image === 'string' ? candidate.image : undefined,
                link: typeof candidate.link === 'string' ? candidate.link : undefined,
                openInNewTab:
                    typeof candidate.openInNewTab === 'boolean'
                        ? candidate.openInNewTab
                        : undefined,
                bgColor: typeof candidate.bgColor === 'string' ? candidate.bgColor : undefined,
                textColor:
                    typeof candidate.textColor === 'string' ? candidate.textColor : undefined,
            },
        ];
    });
    if (cards.length === 0) return null;
    return {
        title: typeof config.title === 'string' ? config.title : undefined,
        columnsPerRow:
            typeof config.columnsPerRow === 'number' && config.columnsPerRow > 0
                ? config.columnsPerRow
                : DEFAULT_COLUMNS_PER_ROW,
        cards,
    };
}

export interface CompletenessWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    countedFields: Array<{ contentTypeGroup: string; contentType: string }>;
    pageSize: number;
}

export interface TranslationCoverageWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    languages?: string[];
    pageSize: number;
}

export interface ReviewPublishWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    pageSize: number;
}

export const DEFAULT_COMPLETENESS_PAGE_SIZE = 25;

function parsePageSize(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
        ? Math.floor(value)
        : DEFAULT_COMPLETENESS_PAGE_SIZE;
}

function parseCountedFields(value: unknown): Array<{
    contentTypeGroup: string;
    contentType: string;
}> {
    if (!Array.isArray(value)) return [];
    return value.flatMap(raw => (isContentTypeRef(raw) ? [raw] : []));
}

// The four completion-dashboard widgets share their branch scoping, so their configs are always
// valid: an omitted hierarchyId falls back to the page hierarchy and an omitted rootNodeId means
// the whole hierarchy.
export function parseCompletenessWidgetConfig(
    config?: Record<string, unknown>,
): CompletenessWidgetConfig {
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        countedFields: parseCountedFields(config?.countedFields),
        pageSize: parsePageSize(config?.pageSize),
    };
}

export function parseTranslationCoverageConfig(
    config?: Record<string, unknown>,
): TranslationCoverageWidgetConfig {
    const languages = Array.isArray(config?.languages)
        ? (config?.languages as unknown[]).filter(
              (value): value is string => typeof value === 'string' && value.length > 0,
          )
        : undefined;
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        languages: languages && languages.length > 0 ? languages : undefined,
        pageSize: parsePageSize(config?.pageSize),
    };
}

export function parseReviewPublishConfig(
    config?: Record<string, unknown>,
): ReviewPublishWidgetConfig {
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        pageSize: parsePageSize(config?.pageSize),
    };
}

export interface ChannelMatrixWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    channelCodes?: string[];
}

export interface ChannelNotReadyWidgetConfig {
    hierarchyId?: string;
    rootNodeId?: string;
    channelCode?: string;
    pageSize: number;
}

export function parseChannelMatrixConfig(
    config?: Record<string, unknown>,
): ChannelMatrixWidgetConfig {
    const channelCodes = Array.isArray(config?.channelCodes)
        ? (config?.channelCodes as unknown[]).filter(
              (value): value is string => typeof value === 'string' && value.length > 0,
          )
        : undefined;
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        channelCodes: channelCodes && channelCodes.length > 0 ? channelCodes : undefined,
    };
}

export function parseChannelNotReadyConfig(
    config?: Record<string, unknown>,
): ChannelNotReadyWidgetConfig {
    return {
        hierarchyId: parseNonEmptyString(config?.hierarchyId),
        rootNodeId: parseNonEmptyString(config?.rootNodeId),
        channelCode: parseNonEmptyString(config?.channelCode),
        pageSize: parsePageSize(config?.pageSize),
    };
}

export interface AnalyticsWidgetConfig {
    eventType: 'page_view' | 'action';
    limit: number;
    periodDays: number;
    bucket: 'day' | 'week';
}

const clampInt = (value: unknown, min: number, max: number, fallback: number) =>
    typeof value === 'number' && Number.isInteger(value)
        ? Math.min(Math.max(value, min), max)
        : fallback;

export function parseAnalyticsWidgetConfig(
    config?: Record<string, unknown>,
): AnalyticsWidgetConfig {
    return {
        eventType: config?.eventType === 'action' ? 'action' : 'page_view',
        limit: clampInt(config?.limit, 1, 50, 10),
        periodDays: clampInt(config?.periodDays, 1, 365, 30),
        bucket: config?.bucket === 'week' ? 'week' : 'day',
    };
}

export function parseActionBarConfig(config?: Record<string, unknown>): ActionBarConfig {
    const rawActions = Array.isArray(config?.actions) ? (config?.actions as unknown[]) : [];
    const actions: ActionBarAction[] = rawActions.flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const candidate = raw as {
            type?: unknown;
            contentTypeGroups?: unknown;
            contentTypeGroup?: unknown;
            contentType?: unknown;
            iconOnly?: unknown;
            templateIds?: unknown;
        };
        if (typeof candidate.type !== 'string') return [];
        return [
            {
                type: candidate.type,
                contentTypeGroups: Array.isArray(candidate.contentTypeGroups)
                    ? (candidate.contentTypeGroups as string[])
                    : undefined,
                contentTypeGroup:
                    typeof candidate.contentTypeGroup === 'string'
                        ? candidate.contentTypeGroup
                        : undefined,
                contentType:
                    typeof candidate.contentType === 'string' ? candidate.contentType : undefined,
                iconOnly: candidate.iconOnly === true,
                templateIds: Array.isArray(candidate.templateIds)
                    ? candidate.templateIds.filter((id): id is string => typeof id === 'string')
                    : undefined,
            },
        ];
    });
    const backgroundColor = config?.backgroundColor === 'transparent' ? 'transparent' : 'primary';
    const showTitle = config?.showTitle === true;
    const titleContentTypeCode =
        typeof config?.titleContentTypeCode === 'string' ? config.titleContentTypeCode : undefined;
    const infoItems: ActionBarInfoItem[] = (
        Array.isArray(config?.infoItems) ? (config?.infoItems as unknown[]) : []
    ).flatMap(raw => {
        if (typeof raw !== 'object' || raw === null) return [];
        const item = raw as { contentTypeCode?: unknown; label?: unknown; icon?: unknown };
        if (typeof item.contentTypeCode !== 'string' || !item.contentTypeCode) return [];
        return [
            {
                contentTypeCode: item.contentTypeCode,
                label: typeof item.label === 'string' ? item.label : undefined,
                icon: typeof item.icon === 'string' ? item.icon : undefined,
            },
        ];
    });
    const titleStyle = config?.titleStyle === 'section' ? 'section' : 'plain';
    return { actions, backgroundColor, showTitle, titleContentTypeCode, titleStyle, infoItems };
}
